import { CSSProperties, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import Map, { MapMouseEvent, MapProvider, MapRef, Marker, Popup } from 'react-map-gl/mapbox';
import { IPersonStatus } from '~/utils/interface/map';
import styles from '../Map.module.scss';
import MapCustomControls from './MapCustomControls';
import MapMeasureLayer from './MapMeasureLayer';
import MapStatusFilter, { MapStatusFilterValue } from './MapStatusFilter';
import { useMapSettings } from '../hooks/useMapSettings';
import { useMapRuler } from '../hooks/useMapRuler';
import type { TrackingDevice } from '../mapTrackingTypes';
import UserMapMarker from './UserMapMarker';
import UserDetailPopup from './UserDetailPopup';
import GeofenceLayers from './GeofenceLayers';
import type { IMapStatisticDatum, IResolvedGeofenceItem } from '~/utils/interface/map';
import { toFiniteNumber } from '~/utils/number';
import {
  ADMIN_MAP_INITIAL_ZOOM,
  ADMIN_MAP_MAX_ZOOM,
  ADMIN_MAP_MIN_ZOOM,
  INITIAL_MAP_CONFIG,
  MARKER_POPUP_OFFSET,
  PROVINCE_MAP_INITIAL_ZOOM,
  PROVINCE_MAP_MAX_ZOOM,
  STATISTIC_CIRCLE_LAYER_ID,
  WARD_MAP_SELECT_FLY_DURATION_MS,
  WARD_MAP_SELECT_FLY_ZOOM,
  getWardMapUserFlyToOffset,
} from '~/constants/map';
import { statisticTierHexByTotal } from '~/constants/statisticLayerTheme';
import LoadingSpinner from './LoadingSpinner';
import StatisticDetailPopup from './StatisticDetailPopup';
import StatisticLayer from './StatisticLayer';

const cx = classNames.bind(styles);

function statisticPopupFromRenderedFeature(feat: {
  geometry?: { type?: string; coordinates?: unknown };
  properties?: Record<string, unknown> | null;
}): { lng: number; lat: number; total: number; name: string } | null {
  if (feat.geometry?.type !== 'Point' || !Array.isArray(feat.geometry.coordinates)) {
    return null;
  }
  const coords = feat.geometry.coordinates as number[];
  if (coords.length < 2 || !Number.isFinite(coords[0]) || !Number.isFinite(coords[1])) return null;
  const [lng, lat] = coords;
  const raw = feat.properties ?? {};
  return {
    lng,
    lat,
    total: Math.max(0, (toFiniteNumber(raw.total) ?? Number(raw.total)) || 0),
    name: String(raw.name ?? ''),
  };
}

function statisticPopupNameWithoutCityProvincePrefix(raw: string): string {
  return raw.replace(/^\s*(?:Thành phố|Tỉnh)\s+/i, '').trim();
}

interface MapBoxProps {
  devices?: TrackingDevice[];
  devicesForStatusFilterCounts?: TrackingDevice[];
  geofences?: IResolvedGeofenceItem[];
  isLoading?: boolean;
  hoveredUserId?: number | null;
  onHoveredUserChange?: (userId: number | null) => void;
  selectedUserId?: number | null;
  onSelectUser?: (userId: number | null) => void;
  personStatuses?: IPersonStatus[];
  errorMessage?: string | null;
  onRetry?: () => void;
  variant?: 'ward' | 'admin' | 'province';
  statisticData?: IMapStatisticDatum[];
  initialCenter?: { lng: number; lat: number } | null;
  /**
   * Cho phép đồng bộ trạng thái filter với cha (dùng để cùng lọc danh sách bên ngoài).
   * Nếu không truyền `statusFilter`, MapBox vẫn quản state nội bộ như cũ.
   */
  statusFilter?: MapStatusFilterValue;
  onStatusFilterChange?: (value: MapStatusFilterValue) => void;
}

export interface MapBoxHandle {
  flyToUser: (lng: number, lat: number) => void;
}

function MapBox({
  devices = [],
  devicesForStatusFilterCounts: devicesForStatusFilterCountsProp,
  geofences = [],
  isLoading = false,
  hoveredUserId = null,
  onHoveredUserChange = () => {},
  selectedUserId = null,
  onSelectUser = () => {},
  personStatuses = [],
  errorMessage = null,
  onRetry,
  variant = 'ward',
  statisticData = [],
  initialCenter,
  statusFilter: statusFilterProp,
  onStatusFilterChange,
}: MapBoxProps, ref: React.Ref<MapBoxHandle>) {
  const { t } = useTranslation('admin/map');
  const isStatisticMap = variant === 'admin' || variant === 'province';
  const safeStatisticData = Array.isArray(statisticData) ? statisticData : [];
  const nfVi = useMemo(() => new Intl.NumberFormat('vi-VN'), []);
  const [statisticPopup, setStatisticPopup] = useState<{
    lng: number;
    lat: number;
    total: number;
    name: string;
  } | null>(null);
  const statisticAccentHex = useMemo(
    () => (statisticPopup != null ? statisticTierHexByTotal(statisticPopup.total) : ''),
    [statisticPopup]
  );

  const statisticMapMaxZoom = useMemo(
    () => (variant === 'province' ? PROVINCE_MAP_MAX_ZOOM : ADMIN_MAP_MAX_ZOOM),
    [variant]
  );
  const mapboxAccessToken = process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN?.trim() ?? '';
  const isMapboxTokenMissing = mapboxAccessToken.length === 0;
  const [internalStatusFilter, setInternalStatusFilter] = useState<MapStatusFilterValue>('all');
  const statusFilter = statusFilterProp ?? internalStatusFilter;
  const handleStatusFilterChange = useCallback(
    (next: MapStatusFilterValue) => {
      if (onStatusFilterChange) onStatusFilterChange(next);
      if (statusFilterProp === undefined) setInternalStatusFilter(next);
    },
    [onStatusFilterChange, statusFilterProp]
  );
  const mapRef = useRef<MapRef | null>(null);
  const [mapGlReady, setMapGlReady] = useState(false);
  const devicesForStatusFilterCounts =
    devicesForStatusFilterCountsProp !== undefined ? devicesForStatusFilterCountsProp : devices;

  const flyToUser = useCallback((lng: number, lat: number) => {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    map.flyTo({
      center: [lng, lat],
      zoom: Math.max(map.getZoom(), WARD_MAP_SELECT_FLY_ZOOM),
      offset: getWardMapUserFlyToOffset(),
      duration: WARD_MAP_SELECT_FLY_DURATION_MS,
      essential: true,
    });
  }, []);

  useImperativeHandle(ref, () => ({ flyToUser }), [flyToUser]);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!mapGlReady || !map || variant !== 'province' || !initialCenter) return;
    const lng = initialCenter.lng;
    const lat = initialCenter.lat;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

    const runFly = () => {
      if (!map.isStyleLoaded()) return;
      console.log('Map FlyTo Center:', initialCenter);
      map.flyTo({
        center: [lng, lat],
        zoom: PROVINCE_MAP_INITIAL_ZOOM,
        duration: 950,
        essential: true,
      });
    };

    if (map.isStyleLoaded()) {
      runFly();
    } else {
      map.once('style.load', runFly);
    }

    return () => {
      map.off('style.load', runFly);
    };
  }, [mapGlReady, variant, initialCenter]);

  const { layer, setLayer, brightness, setBrightness, mapStyle } = useMapSettings();
  const {
    isMeasuring,
    measurePoints,
    totalDistance,
    formattedDistance,
    toggleRuler,
    addPoint,
    removeLastPoint,
    resetMeasure,
  } = useMapRuler();

  useEffect(() => {
    if (!isStatisticMap) {
      setStatisticPopup(null);
      const canvas = mapRef.current?.getMap?.()?.getCanvas();
      if (canvas) canvas.style.cursor = '';
    }
  }, [isStatisticMap]);

  useEffect(() => {
    const mapInstance = mapRef.current?.getMap();
    if (!mapInstance) return;
    if (isMeasuring) {
      mapInstance.doubleClickZoom.disable();
    } else {
      mapInstance.doubleClickZoom.enable();
    }
  }, [isMeasuring]);

  const handleMapMouseMove = useCallback(
    (e: MapMouseEvent) => {
      if (!isStatisticMap || isMeasuring) return;
      const map = mapRef.current?.getMap?.();
      if (!map) return;
      const feats = map.queryRenderedFeatures(e.point, { layers: [STATISTIC_CIRCLE_LAYER_ID] });
      map.getCanvas().style.cursor = feats.length > 0 ? 'pointer' : '';
    },
    [isStatisticMap, isMeasuring]
  );

  const handleMapMouseLeave = useCallback(() => {
    if (!isStatisticMap || isMeasuring) return;
    const mapCanvas = mapRef.current?.getMap?.()?.getCanvas();
    if (mapCanvas) mapCanvas.style.cursor = '';
  }, [isStatisticMap, isMeasuring]);

  const handleMapClick = useCallback(
    (e: MapMouseEvent) => {
      if (isMeasuring) {
        addPoint(e.lngLat.lng, e.lngLat.lat);
        return;
      }
      if (!isStatisticMap) return;
      const map = mapRef.current?.getMap?.();
      if (!map) return;
      const feats = map.queryRenderedFeatures(e.point, { layers: [STATISTIC_CIRCLE_LAYER_ID] });
      const payload = feats[0] ? statisticPopupFromRenderedFeature(feats[0]) : null;
      setStatisticPopup(payload);
    },
    [addPoint, isStatisticMap, isMeasuring]
  );

  const handleMapDoubleClick = useCallback(
    (e: MapMouseEvent) => {
      if (!isMeasuring) return;
      e.preventDefault();
      toggleRuler();
    },
    [isMeasuring, toggleRuler]
  );

  const selectedDevice = useMemo(() => {
    if (isStatisticMap) return null;
    return devices.find((device) => device.person_id === selectedUserId) || null;
  }, [devices, isStatisticMap, selectedUserId]);

  const statisticDetailRows = useMemo(() => {
    if (!statisticPopup) return [];
    const trimmed = statisticPopup.name.trim();
    let nameDisplay =
      trimmed === ''
        ? t('common.notAvailable')
        : variant === 'admin'
          ? statisticPopupNameWithoutCityProvincePrefix(trimmed) || trimmed
          : trimmed;
    const totalFmt = nfVi.format(statisticPopup.total);
    if (variant === 'province') {
      return [
        { label: t('mapBox.statisticLabelWardName'), value: nameDisplay },
        { label: t('mapBox.total2'), value: totalFmt },
      ];
    }
    return [
      { label: t('mapBox.statisticLabelProvince'), value: nameDisplay },
      { label: t('mapBox.total'), value: totalFmt },
    ];
  }, [nfVi, statisticPopup, t, variant]);

  const onMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const layers = map.getStyle().layers ?? [];

    const seaLabelLayer = layers.find((layer) => layer.id === 'water-label-sm' || layer.id === 'water-label-md');

    if (seaLabelLayer) {
      map.setFilter(seaLabelLayer.id, ['==', ['get', 'name_en'], 'South China Sea']);
      map.setLayoutProperty(seaLabelLayer.id, 'text-field', t('mapBox.southChinaSea'));
    }

    for (const layer of layers) {
      if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
        if (layer.id.endsWith('-label')) {
          map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', `name_vi`], ['get', 'name_en']]);
        }
      }
    }

    setMapGlReady(true);
  }, [t]);

  const markerDevices = useMemo(() => {
    if (isStatisticMap) return [];
    const validDevices = devices.filter(
      (device) =>
        Number.isFinite(device.latitude) &&
        Number.isFinite(device.longitude) &&
        device.latitude !== null &&
        device.longitude !== null
    );

    if (statusFilter === 'all') return validDevices;
    return validDevices.filter((device) => device.status === statusFilter);
  }, [devices, isStatisticMap, statusFilter]);

  const selectedUserGeofences = useMemo(() => {
    if (isStatisticMap || !selectedUserId) return [];
    const selectedId = Number(selectedUserId);
    return geofences.filter((geofence) => Number(geofence.personId) === selectedId);
  }, [geofences, isStatisticMap, selectedUserId]);

  return (
    <Box
      className={cx('MapBox', 'w-full h-full', 'map-box-shell')}
      data-device-count={isStatisticMap ? statisticData.length : devices.length}
    >
      <Box className={cx('flex flex-col', 'map-box-body')}>
        {isLoading && (
          <LoadingSpinner label={isStatisticMap ? t('mapBox.loadingStatistic') : t('mapBox.loadingMarkers')} />
        )}
        {errorMessage ? (
          <Alert
            severity='error'
            className='absolute top-14 left-4 z-20 text-xs shadow-sm max-w-[min(90vw,420px)]'
            action={
              onRetry ? (
                <Button color='inherit' size='small' onClick={onRetry}>
                  {t('mapBox.retry')}
                </Button>
              ) : undefined
            }
          >
            <Typography component='span' variant='body2' className='text-inherit'>
              {errorMessage}
            </Typography>
          </Alert>
        ) : null}
        {isMapboxTokenMissing ? (
          <Alert severity='warning' className='absolute top-4 left-4 z-20 text-xs shadow-sm max-w-[min(90vw,420px)]'>
            <Typography component='span' variant='body2' className='text-inherit'>
              {t('mapBox.missingMapboxToken')}
            </Typography>
          </Alert>
        ) : null}
        {variant === 'ward' && (
          <Box className='absolute top-4 right-4 z-20'>
            <MapStatusFilter
              value={statusFilter}
              onFilterChange={handleStatusFilterChange}
              personStatuses={personStatuses}
              devicesForStatusFilterCounts={devicesForStatusFilterCounts}
            />
          </Box>
        )}
        <Box
          className={cx('w-full h-full flex-1 min-h-0 border border-[#DFDFDF]', 'map-box-canvas')}
          style={{ '--map-brightness': `${brightness}%` } as CSSProperties}
        >
          <MapProvider>
            <Box className={cx('map-container')}>
              <Map
                ref={mapRef}
                mapboxAccessToken={mapboxAccessToken}
                initialViewState={{
                  longitude: initialCenter?.lng ?? INITIAL_MAP_CONFIG.lng,
                  latitude: initialCenter?.lat ?? INITIAL_MAP_CONFIG.lat,
                  zoom: isStatisticMap
                    ? variant === 'province'
                      ? PROVINCE_MAP_INITIAL_ZOOM
                      : ADMIN_MAP_INITIAL_ZOOM
                    : INITIAL_MAP_CONFIG.zoom,
                }}
                maxZoom={isStatisticMap ? statisticMapMaxZoom : undefined}
                minZoom={isStatisticMap ? ADMIN_MAP_MIN_ZOOM : undefined}
                mapStyle={mapStyle}
                onLoad={onMapLoad}
                onClick={handleMapClick}
                onMouseMove={handleMapMouseMove}
                onMouseLeave={handleMapMouseLeave}
                onDblClick={handleMapDoubleClick}
                cursor={isMeasuring ? 'crosshair' : undefined}
              >
                <MapCustomControls
                  layer={layer}
                  brightness={brightness}
                  onLayerChange={setLayer}
                  onBrightnessChange={setBrightness}
                  isMeasuring={isMeasuring}
                  onToggleRuler={toggleRuler}
                  onUndoMeasure={removeLastPoint}
                  onResetMeasure={resetMeasure}
                  formattedMeasureDistance={formattedDistance}
                  measurePointCount={measurePoints.length}
                  geolocateMaxZoom={isStatisticMap ? statisticMapMaxZoom : undefined}
                />
                {isStatisticMap && (
                  <>
                    <StatisticLayer statisticData={safeStatisticData} />
                    {statisticPopup && (
                      <Popup
                        longitude={statisticPopup.lng}
                        latitude={statisticPopup.lat}
                        anchor='bottom'
                        closeButton={false}
                        closeOnClick={false}
                        offset={[0, -20]}
                        maxWidth={'500px'}
                        className='statistic-detail-popup'
                      >
                        <StatisticDetailPopup accentHex={statisticAccentHex} rows={statisticDetailRows} />
                      </Popup>
                    )}
                  </>
                )}
                <MapMeasureLayer
                  measurePoints={measurePoints}
                  totalDistance={totalDistance}
                  formattedDistance={formattedDistance}
                />
                {!isStatisticMap && <GeofenceLayers geofences={selectedUserGeofences} />}
                {!isStatisticMap &&
                  markerDevices.map((device) => (
                    <Marker
                      longitude={device.longitude as number}
                      latitude={device.latitude as number}
                      anchor='bottom'
                      key={device.person_id}
                      style={{
                        zIndex: selectedUserId ? (selectedUserId === device.person_id ? 99 : 1) : 10,
                      }}
                    >
                      <UserMapMarker
                        device={device}
                        isHovered={hoveredUserId === device.person_id}
                        isDimmed={Boolean(selectedUserId) && selectedUserId !== device.person_id}
                        isSelected={selectedUserId === device.person_id}
                        onHoverChange={onHoveredUserChange}
                        onSelect={onSelectUser}
                        personStatuses={personStatuses}
                      />
                    </Marker>
                  ))}

                {!isStatisticMap &&
                  selectedDevice &&
                  selectedDevice.latitude !== null &&
                  selectedDevice.longitude !== null && (
                    <Popup
                      longitude={selectedDevice.longitude}
                      latitude={selectedDevice.latitude}
                      anchor='bottom'
                      closeButton={false}
                      closeOnClick={false}
                      onClose={() => onSelectUser(null)}
                      offset={MARKER_POPUP_OFFSET}
                      className='user-detail-popup !z-[140]'
                    >
                      <UserDetailPopup
                        user={selectedDevice}
                        onClose={() => onSelectUser(null)}
                        personStatuses={personStatuses}
                      />
                    </Popup>
                  )}
              </Map>
            </Box>
          </MapProvider>
        </Box>
      </Box>
    </Box>
  );
}

export default forwardRef(MapBox);
